import { useFormikContext } from "formik";
import Card from "../Card";
import SectionHeader from "../SectionHeader";
import SourceBanner from "../SourceBanner";
import StatusButton from "../StatusButton";
import Input from "../../../../../Components/Common/Input/Input";
import ErrorMessage from "../../../../../Components/Common/ErrorMessage/ErrorMessage";
import { autoFillClass, prefillProps } from "../../utils/prefilled";
import DateSelect from "../../../../../Components/Common/DateSelect/DateSelect";
import { PreconFormValues, DocumentRow } from "../../types/precon.types";
import { CHAR_LIMIT } from "../../../../../utils/Common/Constant";

interface Props {
  prefilledFields: Set<string>;
}

const Step1ProjectSetup = ({ prefilledFields }: Props) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    useFormikContext<PreconFormValues>();
  const af = (field: keyof PreconFormValues) =>
    autoFillClass(field, prefilledFields);
  const pf = (field: keyof PreconFormValues) =>
    prefillProps(field, prefilledFields);

  const err = (field: keyof PreconFormValues) =>
    errors[field] as string | undefined;
  const isTouched = (field: keyof PreconFormValues) =>
    touched[field] as boolean | undefined;

  const hasErr = (
    field: keyof PreconFormValues,
    have?: number,
    min?: number,
  ): boolean => {
    const val = String(values[field] ?? "");
    const len = val.length;
    if (have !== undefined && len > have) return true;
    if (min !== undefined && len > 0 && len < min) return true;
    return !!(isTouched(field) && err(field));
  };

  const renderDocList = (side: "docsLeft" | "docsRight") =>
    (values[side] as DocumentRow[]).map((doc) => (
      <div className="ynRow" key={doc.id}>
        <span className="ynLabel">{doc.label}</span>
        <div className="statusSel">
          <StatusButton status={doc.status} onChange={() => {}} />
        </div>
      </div>
    ));
  return (
    <div className="screen active">
      <SectionHeader
        title="Project Setup"
        subtitle="Auto-filled from the project record and GC pre-construction checklist. Edit any field as needed."
      />

      <SourceBanner
        icon="✅"
        title="GC Pre-Construction Checklist Submitted"
        text="Fields below have been pre-filled from the platform. Edit as needed."
      />

      <Card title="Project Parties" badge="Auto-filled" badgeType="auto">
        <div className="fieldRow c2">
          <div className="field">
            <label>Bank / Lender</label>
            <Input
              name="bank"
              className={`${af("bank")}${hasErr("bank", CHAR_LIMIT.NAME) ? " error-input-red" : ""}`}
              value={values.bank}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.NAME}
              {...pf("bank")}
            />
            <ErrorMessage
              value={values.bank}
              have={CHAR_LIMIT.NAME}
              touched={isTouched("bank")}
              error={err("bank")}
            />
          </div>
          <div className="field">
            <label>Lending Officer</label>
            <Input
              name="lendingOfficer"
              className={`${af("lendingOfficer")}${hasErr("lendingOfficer", CHAR_LIMIT.NAME) ? " error-input-red" : ""}`}
              value={values.lendingOfficer}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.NAME}
              {...pf("lendingOfficer")}
            />
            <ErrorMessage
              value={values.lendingOfficer}
              have={CHAR_LIMIT.NAME}
              touched={isTouched("lendingOfficer")}
              error={err("lendingOfficer")}
            />
          </div>
        </div>

        <div className="fieldRow c3">
          <div className="field">
            <label>
              Project Name <span className="req">*</span>
            </label>
            <Input
              name="projectName"
              className={`${af("projectName")}${hasErr("projectName", CHAR_LIMIT.NAME) ? " error-input-red" : ""}`}
              value={values.projectName}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.NAME}
              {...pf("projectName")}
            />
            <ErrorMessage
              value={values.projectName}
              have={CHAR_LIMIT.NAME}
              touched={isTouched("projectName")}
              error={err("projectName")}
            />
          </div>
          <div className="field">
            <label>Description</label>
            <Input
              name="projectDescription"
              className={`${af("projectDescription")}${hasErr("projectDescription", CHAR_LIMIT.DESCRIPTION) ? " error-input-red" : ""}`}
              value={values.projectDescription}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.DESCRIPTION}
              {...pf("projectDescription")}
            />
            <ErrorMessage
              value={values.projectDescription}
              have={CHAR_LIMIT.DESCRIPTION}
              touched={isTouched("projectDescription")}
              error={err("projectDescription")}
            />
          </div>
          <div className="field">
            <label>Project Type</label>
            <Input
              name="projectType"
              className={`${af("projectType")}${hasErr("projectType", CHAR_LIMIT.NAME) ? " error-input-red" : ""}`}
              value={values.projectType}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.NAME}
              {...pf("projectType")}
            />
            <ErrorMessage
              value={values.projectType}
              have={CHAR_LIMIT.NAME}
              touched={isTouched("projectType")}
              error={err("projectType")}
            />
          </div>
        </div>

        <div className="fieldRow c2">
          <div className="field">
            <label>Owner / Borrower</label>
            <Input
              name="ownerName"
              className={`${af("ownerName")}${hasErr("ownerName", CHAR_LIMIT.NAME) ? " error-input-red" : ""}`}
              value={values.ownerName}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.NAME}
              {...pf("ownerName")}
            />
            <ErrorMessage
              value={values.ownerName}
              have={CHAR_LIMIT.NAME}
              touched={isTouched("ownerName")}
              error={err("ownerName")}
            />
          </div>
          <div className="field">
            <label>Contractor / GC</label>
            <Input
              name="gcName"
              className={`${af("gcName")}${hasErr("gcName", CHAR_LIMIT.NAME) ? " error-input-red" : ""}`}
              value={values.gcName}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.NAME}
              {...pf("gcName")}
            />
            <ErrorMessage
              value={values.gcName}
              have={CHAR_LIMIT.NAME}
              touched={isTouched("gcName")}
              error={err("gcName")}
            />
          </div>
        </div>

        <div className="fieldRow c2">
          <div className="field">
            <label>Architect</label>
            <Input
              name="architectName"
              className={`${af("architectName")}${hasErr("architectName", CHAR_LIMIT.NAME) ? " error-input-red" : ""}`}
              value={values.architectName}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.NAME}
              {...pf("architectName")}
            />
            <ErrorMessage
              value={values.architectName}
              have={CHAR_LIMIT.NAME}
              touched={isTouched("architectName")}
              error={err("architectName")}
            />
          </div>
          <div className="field">
            <label>Services Requested</label>
            <Input
              name="servicesRequested"
              className={`fi${hasErr("servicesRequested", CHAR_LIMIT.SHORT) ? " error-input-red" : ""}`}
              value={values.servicesRequested}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.SHORT}
            />
            <ErrorMessage
              value={values.servicesRequested}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("servicesRequested")}
              error={err("servicesRequested")}
            />
          </div>
        </div>
      </Card>

      <Card title="Report Details" badge="Editable" badgeType="edit">
        <div className="fieldRow c4">
          <div className="field">
            <label>
              Report Date <span className="req">*</span>
            </label>
            <DateSelect
              value={values.reportDate}
              onChange={(val) => setFieldValue("reportDate", val)}
              error={hasErr("reportDate")}
            />
            <ErrorMessage
              touched={isTouched("reportDate")}
              error={err("reportDate")}
            />
          </div>
          <div className="field">
            <label>
              Invoice Number <span className="req">*</span>
            </label>
            <Input
              name="invoiceNumber"
              className={`${af("invoiceNumber")}${hasErr("invoiceNumber", CHAR_LIMIT.SHORT) ? " error-input-red" : ""}`}
              value={values.invoiceNumber}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.SHORT}
              {...pf("invoiceNumber")}
            />
            <ErrorMessage
              value={values.invoiceNumber}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("invoiceNumber")}
              error={err("invoiceNumber")}
            />
          </div>
          <div className="field">
            <label>
              Invoice Amount <span className="req">*</span>
            </label>
            <div className="inputWithPrefix">
              <span className="prefixSymbol">$</span>
              <Input
                name="invoiceAmount"
                className={`${af("invoiceAmount")}${hasErr("invoiceAmount", CHAR_LIMIT.SHORT) ? " error-input-red" : ""}`}
                type="number"
                value={values.invoiceAmount}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={CHAR_LIMIT.SHORT}
                placeholder="0.00"
                {...pf("invoiceAmount")}
              />
            </div>
            <ErrorMessage
              value={values.invoiceAmount}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("invoiceAmount")}
              error={err("invoiceAmount")}
            />
          </div>
          <div className="field">
            <label>
              Prepared By <span className="req">*</span>
            </label>
            <Input
              name="preparedBy"
              className={`fi${hasErr("preparedBy", CHAR_LIMIT.NAME) ? " error-input-red" : ""}`}
              value={values.preparedBy}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.NAME}
            />
            <ErrorMessage
              value={values.preparedBy}
              have={CHAR_LIMIT.NAME}
              touched={isTouched("preparedBy")}
              error={err("preparedBy")}
            />
          </div>
        </div>
        <div className="fieldRow c2">
          <div className="field">
            <label>Project Address</label>
            <Input
              name="projectAddress"
              className={`${af("projectAddress")}${hasErr("projectAddress", CHAR_LIMIT.ADDRESS) ? " error-input-red" : ""}`}
              value={values.projectAddress}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.ADDRESS}
              {...pf("projectAddress")}
            />
            <ErrorMessage
              value={values.projectAddress}
              have={CHAR_LIMIT.ADDRESS}
              touched={isTouched("projectAddress")}
              error={err("projectAddress")}
            />
          </div>
          <div className="field">
            <label>Inspection / Review Date</label>
            <DateSelect
              value={values.reviewDate}
              onChange={(val) => setFieldValue("reviewDate", val)}
            />
          </div>
        </div>
      </Card>

      {(values.docsLeft.length > 0 || values.docsRight.length > 0) && (
        <Card
          title="Documents Received"
          badge="From GC Checklist"
          badgeType="auto"
        >
          <div className="fieldRow c2">
            <div>{renderDocList("docsLeft")}</div>
            <div>{renderDocList("docsRight")}</div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Step1ProjectSetup;
